import { Progress } from "@mantine/core";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import { type UIEvent, useCallback, useEffect, useRef } from "react";
import { GroupSearchRepository } from "shared/components/organization/groups/data";
import classes from "shared/components/organization/groups/groups.module.css";
import { groupTableDefinition } from "shared/components/organization/groups/GroupTableDefinition";
import { permissionCheck, usePermissions } from "shared/stores/oidc";
import { logger } from "shared/utils/logger";
import { notify } from "shared/utils/notify";
import { Permission } from "src/gql/graphql";
import { container } from "tsyringe";

export const GroupSearchTable = (props: { groupSearch: string }) => {
    const nav = useNavigate();
    const search = useSearch({ from: "/_auth/organization/groups" });

    const tableContainerRef = useRef<HTMLDivElement>(null);
    const containerScrollHeight = tableContainerRef.current?.scrollHeight;
    const containerClientHeight = tableContainerRef.current?.clientHeight;

    const { permissions } = usePermissions();

    const loader = container.resolve(GroupSearchRepository).get({ query: props.groupSearch, queryMembership: false });
    const groups = loader.useItems();
    const { isLoading: groupSearchIsLoading, hasNextPage: groupSearchHasNextPage } = loader.useStatus();

    const table = useMantineReactTable({
        columns: groupTableDefinition,
        data: groups,
        meta: {
            canNavigateTo: (groupId: string) => permissionCheck(permissions, Permission.GroupUsersView, groupId),
        },
        enableColumnActions: false,
        enableBottomToolbar: false,
        enablePagination: false,
        enableSelectAll: false,
        enableRowVirtualization: true,
        enableRowPinning: true,
        rowPinningDisplayMode: "top",
        enableSorting: false,
        enableTopToolbar: false,
        getRowId: (row) => row.id,
        mantineTableContainerProps: {
            ref: tableContainerRef,
            style: { height: "70vh", maxHeight: "1900px", flexGrow: 1 },
            onScroll: (event: UIEvent<HTMLDivElement>) => fetchMoreGroups(event.target as HTMLDivElement),
        },
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: (event) => {
                const target = event.target as HTMLElement;
                if (
                    // Checks if we're clicking a badge
                    !target.closest(".mantine-Badge-root") &&
                    // Checks if we're clicking one of the options in the "View All" popover
                    !target.closest(`.${classes.viewMoreDropdownItem}`) &&
                    // Checks if we're clicking the "Select" button (or any action icon)
                    !target.closest(".mantine-ActionIcon-root") &&
                    permissionCheck(permissions, Permission.GroupSubgroupManage, row.id)
                ) {
                    nav({ to: "/organization/groups/$groupId", params: { groupId: row.id }, search: { parent: search.parent } }).catch(
                        notify.catch,
                    );
                }
            },
            style: { cursor: permissionCheck(permissions, Permission.GroupSubgroupManage, row.id) ? "pointer" : undefined },
        }),
        initialState: {
            columnVisibility: { "mrt-row-pin": false },
        },
        state: {
            isLoading: groupSearchIsLoading && groups.length == 0,
        },
        rowVirtualizerOptions: {
            overscan: 5,
        },
    });

    const fetchMoreGroups = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
                //once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
                if (scrollHeight - scrollTop - clientHeight < 200 && !groupSearchIsLoading && groupSearchHasNextPage) {
                    loader.loadMore().catch(logger.error);
                }
            }
        },
        [groupSearchIsLoading, groupSearchHasNextPage],
    );

    useEffect(() => {
        if (
            !!containerScrollHeight &&
            !!containerClientHeight &&
            containerScrollHeight <= containerClientHeight &&
            groupSearchHasNextPage &&
            !groupSearchIsLoading
        ) {
            loader.loadMore().catch(logger.error);
        }
    }, [containerScrollHeight, containerClientHeight, groupSearchHasNextPage, groupSearchIsLoading]);

    return (
        <>
            <MantineReactTable table={table} />
            {groupSearchIsLoading && <Progress value={100} striped animated />}
        </>
    );
};
